import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getDefectQueue, getSubmission, saveDefectLabels, dismissDefectSubmission } from '../api/client';

const TAXONOMY = [
  {
    group: 'Abrasion',
    types: [
      'Color Breaking',
      'Non-Color Breaking',
    ],
  },
  {
    group: 'Corner Defects',
    types: [
      'Bindery Pull',
      'Blunted / Blunt Corner',
      'Corner Chipping',
      'Corner Piece Missing / Chunk Missing',
      'Corner Tear',
      'Creased Corner / Dog-Ear',
      'Fraying / Layer Splitting',
      'Miscut Corner',
      'Uncut Corner / Siamese Corner',
    ],
  },
  {
    group: 'Crease and Bend Defects',
    types: [
      'Crease - Other',
      'Finger Bend',
      'Fold',
      'Reader Crease',
      'Spine Roll',
      'Spine Ticks',
      'Stress Lines',
      'Staple Impression',
      'Subscription Crease',
      'Warping',
    ],
  },
  {
    group: 'Edge Defects',
    types: [
      'Edge Wear',
    ],
  },
  {
    group: 'Foreign Substance Defects',
    types: [
      'Dirt',
      'Extra Staples Added',
      'Ink / Marker / Paint',
      'Stickers / Fingerprints',
      'Tape / Tape Residue',
      'Writing',
      'Stamps',
    ],
  },
  {
    group: 'Manufacturing and Distortion Defects',
    types: [
      'Double Cover',
      'Ink Smear / Ink Skip',
      'Miswrap / Miscut',
      'Siamese Pages',
    ],
  },
  {
    group: 'Missing Part Defects',
    types: [
      'Chipping / Marvel Chipping',
      'Hole Punch',
      'Missing Page / Coupon / Panel',
      'Missing Piece - Small',
      'Missing Piece - Medium',
      'Missing Piece - Large',
      'Tape Pull',
      'Trimming',
    ],
  },
  {
    group: 'Stain and Chemical Defects',
    types: [
      'Foxing',
      'Mold / Mildew / Odor',
      'Rust Migration',
      'Stain - Other',
      'Water / Liquid Damage',
    ],
  },
  {
    group: 'Tear and Separation Defects',
    types: [
      'Bindery / Printer Tear',
      'Detached Cover / Centerfold',
      'Spine Split',
      'Tear',
    ],
  },
];

const DEFECT_COLORS = ['#4a9ff5', '#f5954a', '#4af5a0', '#f54a4a', '#c44af5', '#f5e04a'];

interface BBox { x: number; y: number; w: number; h: number; }
interface DefectEntry { id: string; bbox: BBox | null; category: string; subcategory: string; }
interface PhotoAnnotation { photoIndex: number; url: string; label: string; defects: DefectEntry[]; }
interface QueueItem { submission_id: string; photo_count: number; ai_grade: number; ai_label: string; ai_defects?: any[]; ai_notes?: string; created_at: string; needs_bbox_annotation?: boolean; inline_defect_photo_indices?: number[]; }

let _idSeq = 0;
const newId = () => `d${++_idSeq}`;

// ── Per-photo annotation card ──────────────────────────────────────────────
interface PhotoCardProps {
  annotation: PhotoAnnotation;
  onChange: (defects: DefectEntry[]) => void;
  isSkipped: boolean;
  onMarkDuplicate: () => void;
  onUndoDuplicate: () => void;
  showDuplicateButton: boolean;
}

function PhotoCard({ annotation, onChange, isSkipped, onMarkDuplicate, onUndoDuplicate, showDuplicateButton }: PhotoCardProps) {
  const { url, label, defects, photoIndex } = annotation;
  const svgRef = useRef<SVGSVGElement>(null);
  const [drawing, setDrawing] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [imgError, setImgError] = useState(false);

  const getSVGCoords = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    const { x, y } = getSVGCoords(e);
    setDrawing({ x0: x, y0: y, x1: x, y1: y });
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!drawing) return;
    const { x, y } = getSVGCoords(e);
    setDrawing(d => d ? { ...d, x1: x, y1: y } : null);
  };

  const handleMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!drawing) return;
    const { x, y } = getSVGCoords(e);
    const bbox: BBox = {
      x: Math.min(drawing.x0, x),
      y: Math.min(drawing.y0, y),
      w: Math.abs(x - drawing.x0),
      h: Math.abs(y - drawing.y0),
    };
    setDrawing(null);
    // Only create a box if it has meaningful size
    if (bbox.w > 0.01 && bbox.h > 0.01) {
      onChange([...defects, { id: newId(), bbox, category: '', subcategory: '' }]);
    }
  };

  const addDefectNoRegion = () => {
    onChange([...defects, { id: newId(), bbox: null, category: '', subcategory: '' }]);
  };

  const removeDefect = (id: string) => {
    onChange(defects.filter(d => d.id !== id));
  };

  const updateDefect = (id: string, field: 'category' | 'subcategory', value: string) => {
    onChange(defects.map(d => {
      if (d.id !== id) return d;
      if (field === 'category') return { ...d, category: value, subcategory: '' };
      return { ...d, [field]: value };
    }));
  };

  const copyFromAbove = (id: string, i: number) => {
    if (i === 0) return;
    const prev = defects[i - 1];
    onChange(defects.map(d => d.id !== id ? d : { ...d, category: prev.category, subcategory: prev.subcategory }));
  };

  if (isSkipped) {
    return (
      <div className="dc-photo-card dc-photo-skipped">
        <div className="dc-skip-banner">
          <span className="dc-skip-icon">⊘</span>
          <span>Photo {photoIndex} — marked as duplicate, will not be saved</span>
          <button className="dc-undo-skip-btn" onClick={onUndoDuplicate}>Undo</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dc-photo-card">
      <div className="dc-photo-label">
        <span className="dc-photo-index">Photo {photoIndex}</span>
        <span className="dc-photo-name">{label}</span>
        <span className="dc-draw-hint">Click and drag on the photo to mark a defect region</span>
      </div>

      <div className="dc-image-wrap">
        {imgError ? (
          <div className="dc-img-missing">Photo not available — file was not uploaded</div>
        ) : (
          <img src={url} className="dc-image" draggable={false} alt={label} onError={() => setImgError(true)} />
        )}
        <svg
          ref={svgRef}
          className="dc-svg-overlay"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => setDrawing(null)}
        >
          {defects.filter(d => d.bbox).map((d, i) => {
            const b = d.bbox!;
            const color = DEFECT_COLORS[i % DEFECT_COLORS.length];
            return (
              <g key={d.id}>
                <rect
                  x={`${b.x * 100}%`} y={`${b.y * 100}%`}
                  width={`${b.w * 100}%`} height={`${b.h * 100}%`}
                  fill={color + '33'} stroke={color} strokeWidth="2"
                />
                <text
                  x={`${(b.x + 0.005) * 100}%`} y={`${(b.y + 0.04) * 100}%`}
                  fill={color} fontSize="14" fontWeight="bold"
                  style={{ userSelect: 'none' }}
                >
                  {i + 1}
                </text>
              </g>
            );
          })}
          {drawing && (
            <rect
              x={`${Math.min(drawing.x0, drawing.x1) * 100}%`}
              y={`${Math.min(drawing.y0, drawing.y1) * 100}%`}
              width={`${Math.abs(drawing.x1 - drawing.x0) * 100}%`}
              height={`${Math.abs(drawing.y1 - drawing.y0) * 100}%`}
              fill="rgba(255,255,255,0.08)" stroke="white" strokeWidth="1.5" strokeDasharray="5,4"
            />
          )}
        </svg>
      </div>

      <div className="dc-defect-list">
        {defects.map((d, i) => {
          const color = DEFECT_COLORS[i % DEFECT_COLORS.length];
          const groupObj = TAXONOMY.find(t => t.group === d.category);
          return (
            <div key={d.id} className="dc-defect-row" style={{ borderLeftColor: color }}>
              <div className="dc-defect-num" style={{ color }}>
                {d.bbox ? `Region ${i + 1}` : `Defect ${i + 1}`}
                {!d.bbox && <span className="dc-no-region"> (no region)</span>}
              </div>
              <div className="dc-defect-selects">
                <select
                  className="dc-select"
                  value={d.category}
                  onChange={e => updateDefect(d.id, 'category', e.target.value)}
                >
                  <option value="">— Defect Category —</option>
                  {TAXONOMY.map(t => (
                    <option key={t.group} value={t.group}>{t.group}</option>
                  ))}
                </select>
                <select
                  className="dc-select"
                  value={d.subcategory}
                  disabled={!d.category}
                  onChange={e => updateDefect(d.id, 'subcategory', e.target.value)}
                >
                  <option value="">— Defect Type —</option>
                  {groupObj?.types.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              {i > 0 && (
                <button className="dc-copy-btn" onClick={() => copyFromAbove(d.id, i)} title="Copy category and type from above">
                  ↑ Copy
                </button>
              )}
              <button className="dc-remove-btn" onClick={() => removeDefect(d.id)} title="Remove">✕</button>
            </div>
          );
        })}
        <div className="dc-add-row">
          <button className="dc-add-region-btn" onClick={addDefectNoRegion}>
            + Identify additional defect
          </button>
          {defects.length === 0 && (
            <span className="dc-no-defect-hint">No defects — or draw a region above to start</span>
          )}
        </div>
        {showDuplicateButton && (
          <div className="dc-duplicate-row">
            <button className="dc-mark-duplicate-btn" onClick={onMarkDuplicate}>
              Mark as duplicate — skip this photo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function DefectClassification() {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);

  const [submission, setSubmission] = useState<any | null>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);

  // photoAnnotations keyed by photo index (14, 15, 16, …)
  const [photoAnnotations, setPhotoAnnotations] = useState<Record<number, DefectEntry[]>>({});
  const [skippedPhotos, setSkippedPhotos] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState(false);

  // Load queue on mount
  useEffect(() => {
    setQueueLoading(true);
    getDefectQueue()
      .then(d => setQueue(d.submissions ?? []))
      .catch(e => setQueueError(e.message))
      .finally(() => setQueueLoading(false));
  }, []);

  // Load submission details when queue item changes
  useEffect(() => {
    const item = queue[queueIndex];
    if (!item) return;
    setSubLoading(true);
    setSubError(null);
    setSubmission(null);
    setPhotoAnnotations({});
    setSkippedPhotos(new Set());
    getSubmission(item.submission_id)
      .then(data => {
        setSubmission(data);
        // Pre-populate dropdowns from labels saved in the review page
        if (item.needs_bbox_annotation && data.inline_defect_labels) {
          const initial: Record<number, DefectEntry[]> = {};
          for (const [idx, labels] of Object.entries(data.inline_defect_labels as Record<string, any[]>)) {
            initial[Number(idx)] = (labels as any[]).map(l => ({
              id: newId(),
              bbox: null,
              category: l.category ?? '',
              subcategory: l.subcategory ?? '',
            }));
          }
          setPhotoAnnotations(initial);
        }
      })
      .catch(e => setSubError(e.message))
      .finally(() => setSubLoading(false));
  }, [queue, queueIndex]);

  const currentQueueItem = queue[queueIndex];

  const defectPhotos: { photoIndex: number; url: string; label: string }[] = submission
    ? (() => {
        const all = (submission.photo_urls ?? []).map((p: any, i: number) => ({ ...p, photoIndex: i }));
        if (currentQueueItem?.needs_bbox_annotation) {
          const indices = new Set(currentQueueItem.inline_defect_photo_indices ?? []);
          return all.filter((p: any) => indices.has(p.photoIndex));
        }
        return all.filter((p: any) => p.photoIndex >= 14);
      })()
    : [];

  const handlePhotoChange = useCallback((photoIndex: number, defects: DefectEntry[]) => {
    setPhotoAnnotations(prev => ({ ...prev, [photoIndex]: defects }));
  }, []);

  const handleSubmit = async () => {
    const item = queue[queueIndex];
    if (!item) return;
    setSaving(true);
    setSaveError(null);
    // Build photo_labels: { "14": [{category, subcategory, bbox}], "15": [...] }
    // Exclude photos the user marked as duplicates
    const photoLabels: Record<string, any[]> = {};
    for (const photo of defectPhotos.filter(p => !skippedPhotos.has(p.photoIndex))) {
      const defects = photoAnnotations[photo.photoIndex] ?? [];
      photoLabels[String(photo.photoIndex)] = defects.map(d => ({
        category: d.category,
        subcategory: d.subcategory,
        ...(d.bbox ? { bbox: d.bbox } : {}),
      }));
    }
    try {
      await saveDefectLabels(item.submission_id, photoLabels);
      // Advance to next
      setQueue(prev => prev.filter((_, i) => i !== queueIndex));
      setQueueIndex(prev => Math.max(0, Math.min(prev, queue.length - 2)));
    } catch (e: any) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    setQueueIndex(prev => (prev + 1) % queue.length);
  };

  const handleDismiss = async () => {
    const item = queue[queueIndex];
    if (!item) return;
    setDismissing(true);
    try {
      await dismissDefectSubmission(item.submission_id);
      setQueue(prev => prev.filter((_, i) => i !== queueIndex));
      setQueueIndex(prev => Math.max(0, Math.min(prev, queue.length - 2)));
    } catch (e: any) {
      setSaveError(e.message);
    } finally {
      setDismissing(false);
    }
  };

  if (queueLoading) return <div className="loading">Loading defect queue…</div>;
  if (queueError) return <div className="error-banner">{queueError}</div>;

  if (queue.length === 0) {
    return (
      <div className="dc-empty">
        <div className="dc-empty-icon">✓</div>
        <div className="dc-empty-title">Defect queue is clear</div>
        <div className="dc-empty-sub">All submissions with defect photos have been classified.</div>
      </div>
    );
  }

  return (
    <div className="dc-page">
      <div className="dc-queue-bar">
        <span className="dc-queue-count">{queue.length} submission{queue.length !== 1 ? 's' : ''} need defect classification</span>
        <span className="dc-queue-pos">{queueIndex + 1} of {queue.length}</span>
      </div>

      {subLoading && <div className="loading">Loading photos…</div>}
      {subError && <div className="error-banner">{subError}</div>}

      {submission && (
        <>
          <div className="dc-sub-header">
            <div className="dc-sub-id">{currentQueueItem.submission_id}</div>
            <div className="dc-sub-meta">
              <span className="dc-meta-chip">AI: {currentQueueItem.ai_grade} {currentQueueItem.ai_label}</span>
              {submission.expert_grade && (
                <span className="dc-meta-chip dc-meta-expert">Expert: {submission.expert_grade}</span>
              )}
              {currentQueueItem?.needs_bbox_annotation
                ? <span className="dc-meta-chip dc-meta-inline">Draw boxes on {defectPhotos.length} labeled photo{defectPhotos.length !== 1 ? 's' : ''}</span>
                : <span className="dc-meta-chip dc-meta-photos">{defectPhotos.length} defect photo{defectPhotos.length !== 1 ? 's' : ''}</span>
              }
            </div>
          </div>

          {/* AI defect context — reference only */}
          {(submission.ai_defects?.length > 0 || submission.ai_notes) && (
            <div className="dc-context-box">
              <div className="dc-context-label">AI assessment (reference only — do not copy)</div>
              {submission.ai_defects?.map((d: any, i: number) => (
                <div key={i} className="dc-context-defect">
                  <span className="dc-context-area">{d.area}</span>
                  <span className={`dc-context-sev dc-sev-${d.severity}`}>{d.severity}</span>
                  <span className="dc-context-desc">{d.description}</span>
                </div>
              ))}
              {submission.ai_notes && <div className="dc-context-notes">{submission.ai_notes}</div>}
            </div>
          )}

          {defectPhotos.length === 0 ? (
            <div className="dc-no-defect-photos">
              This submission has no defect photos (photo_count ≤ 14). It should not have appeared in the queue.
            </div>
          ) : (
            defectPhotos.map((photo, i) => (
              <PhotoCard
                key={photo.photoIndex}
                annotation={{
                  photoIndex: photo.photoIndex,
                  url: photo.url,
                  label: photo.label,
                  defects: photoAnnotations[photo.photoIndex] ?? [],
                }}
                onChange={defects => handlePhotoChange(photo.photoIndex, defects)}
                isSkipped={skippedPhotos.has(photo.photoIndex)}
                showDuplicateButton={i > 0}
                onMarkDuplicate={() => setSkippedPhotos(prev => new Set(Array.from(prev).concat(photo.photoIndex)))}
                onUndoDuplicate={() => setSkippedPhotos(prev => { const s = new Set(Array.from(prev)); s.delete(photo.photoIndex); return s; })}
              />
            ))
          )}

          {saveError && <div className="error-banner dc-save-error">{saveError}</div>}

          <div className="dc-action-row">
            <button className="dc-dismiss-btn" onClick={handleDismiss} disabled={saving || dismissing}>
              {dismissing ? 'Dismissing…' : 'Dismiss'}
            </button>
            <button className="dc-skip-btn" onClick={handleSkip} disabled={saving || dismissing || queue.length <= 1}>
              Skip →
            </button>
            <button className="dc-submit-btn" onClick={handleSubmit} disabled={saving || dismissing}>
              {saving ? 'Saving…' : 'Submit & Next →'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
